import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailTemplatesAPI, EmailTemplate, TemplateCategory } from '../../services/api';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Badge, Spinner, EmptyState } from '../ui';

const EmailTemplatesList: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('');
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchTemplates();
  }, [pagination.page, categoryFilter, activeFilter, searchTerm]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await emailTemplatesAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        category: categoryFilter || undefined,
        isActive: activeFilter === '' ? undefined : activeFilter,
        search: searchTerm || undefined,
      });
      setTemplates(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await emailTemplatesAPI.delete(id);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      await emailTemplatesAPI.duplicate(id);
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Failed to duplicate template');
    }
  };

  const getCategoryVariant = (category: TemplateCategory): 'primary' | 'success' | 'warning' | 'error' | 'neutral' | 'secondary' => {
    const variants: Record<TemplateCategory, 'primary' | 'success' | 'warning' | 'error' | 'neutral' | 'secondary'> = {
      WELCOME: 'primary',
      FOLLOW_UP: 'success',
      PROPOSAL: 'secondary',
      THANK_YOU: 'primary',
      REMINDER: 'warning',
      NEWSLETTER: 'primary',
      CUSTOM: 'neutral',
    };
    return variants[category] || 'neutral';
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <Card variant="gradient" className="mb-8">
        <CardBody className="p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h1 className="text-4xl font-bold text-white">Email Templates</h1>
              <p className="mt-2 text-primary-100">
                Create and manage reusable email templates
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => navigate('/emails/templates/new')}
              leftIcon={<PlusIcon className="h-5 w-5" />}
              className="shadow-lg"
            >
              New Template
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Filters */}
      <Card variant="elevated" className="mb-6">
        <CardBody className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | '')}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none"
              >
                <option value="">All Categories</option>
                <option value="WELCOME">Welcome</option>
                <option value="FOLLOW_UP">Follow Up</option>
                <option value="PROPOSAL">Proposal</option>
                <option value="THANK_YOU">Thank You</option>
                <option value="REMINDER">Reminder</option>
                <option value="NEWSLETTER">Newsletter</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>

            {/* Active Filter */}
            <div>
              <select
                value={activeFilter === '' ? '' : activeFilter.toString()}
                onChange={(e) => setActiveFilter(e.target.value === '' ? '' : e.target.value === 'true')}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} variant="elevated" hoverable>
            <CardBody className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-neutral-900 truncate">{template.name}</h3>
                  <p className="text-sm text-neutral-500 mt-1 truncate">{template.subject}</p>
                </div>
                <Badge variant={getCategoryVariant(template.category)} size="sm" className="ml-3">
                  {template.category.replace('_', ' ')}
                </Badge>
              </div>

              {/* Description */}
              {template.description && (
                <p className="text-sm text-neutral-600 mb-4 line-clamp-2">{template.description}</p>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 mb-4 text-sm text-neutral-500">
                <div className="flex items-center">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                  {template._count?.emailHistory || 0} sent
                </div>
                <Badge variant={template.isActive ? 'success' : 'neutral'} size="sm">
                  {template.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t border-neutral-200">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/emails/templates/${template.id}/edit`)}
                  leftIcon={<PencilIcon className="h-4 w-4" />}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDuplicate(template.id)}
                  leftIcon={<DocumentDuplicateIcon className="h-4 w-4" />}
                  className="flex-1"
                >
                  Duplicate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(template.id)}
                  className="text-error-600 hover:bg-error-50"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {templates.length === 0 && !loading && (
        <EmptyState
          icon={<EnvelopeIcon className="h-12 w-12" />}
          title="No templates found"
          description="Get started by creating a new email template."
          action={
            <Button
              variant="primary"
              onClick={() => navigate('/emails/templates/new')}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              New Template
            </Button>
          }
        />
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card variant="elevated" className="mt-8">
          <CardBody className="px-4 py-3">
            <div className="flex flex-1 justify-between sm:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </Button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-neutral-700">
                  Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default EmailTemplatesList;

